"use client";

import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
} from "date-fns";

import { useEffect, useMemo, useRef, useState } from "react";
import { DateRange } from "react-day-picker";

import { useIsMobile } from "@/contexts/is-mobile-provider";

import { useClinicClock } from "@/hooks/use-clinic-clock";
import { useFollowingDefault } from "@/hooks/use-following-default";

import { dayMarker } from "@/lib/clinic-clock";
import { cx } from "@/lib/tailwind-utils";

import Button from "./button";
import { Calendar } from "./calendar";
import Input from "./input";
import { Label } from "./label";

// Range presets for quick selection
interface RangePreset {
  label: string;
  getRange: (today: Date) => DateRange;
}

const PAST_RANGE_PRESETS: RangePreset[] = [
  {
    label: "Last 7 days",
    getRange: (today) => ({
      from: subDays(today, 6),
      to: today,
    }),
  },
  {
    label: "Last 14 days",
    getRange: (today) => ({
      from: subDays(today, 13),
      to: today,
    }),
  },
  {
    label: "Last 30 days",
    getRange: (today) => ({
      from: subDays(today, 29),
      to: today,
    }),
  },
  {
    label: "Last 90 days",
    getRange: (today) => ({
      from: subDays(today, 89),
      to: today,
    }),
  },
  {
    label: "This month",
    getRange: (today) => ({
      from: startOfMonth(today),
      to: today,
    }),
  },
  {
    label: "Last month",
    getRange: (today) => {
      const lastMonth = subMonths(today, 1);
      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
    },
  },
];

const FUTURE_RANGE_PRESETS: RangePreset[] = [
  {
    label: "Next 7 days",
    getRange: (today) => ({
      from: today,
      to: addDays(today, 6),
    }),
  },
  {
    label: "Next 14 days",
    getRange: (today) => ({
      from: today,
      to: addDays(today, 13),
    }),
  },
  {
    label: "Next 30 days",
    getRange: (today) => ({
      from: today,
      to: addDays(today, 29),
    }),
  },
  {
    label: "This week",
    getRange: (today) => {
      const weekStart = startOfWeek(today, { weekStartsOn: 0 });
      return { from: weekStart, to: addDays(weekStart, 6) };
    },
  },
  {
    label: "This month",
    getRange: (today) => ({
      from: today,
      to: endOfMonth(today),
    }),
  },
  {
    label: "Next month",
    getRange: (today) => {
      const nextMonth = addMonths(today, 1);
      return { from: startOfMonth(nextMonth), to: endOfMonth(nextMonth) };
    },
  },
];

type DateDirection = "future" | "past";

interface SmartDatePickerPanelBaseProps {
  onClose?: () => void;
  parseNaturalLanguage?: (input: string) => Date | null;
  autoFocus?: boolean;
  /** Direction for range presets: "future" or "past" (default) */
  direction?: DateDirection;
}

interface SmartDatePickerPanelSingleProps extends SmartDatePickerPanelBaseProps {
  mode?: "single";
  value?: Date;
  onChange?: (date: Date) => void;
}

interface SmartDatePickerPanelRangeProps extends SmartDatePickerPanelBaseProps {
  mode: "range";
  value?: DateRange;
  onChange?: (range: DateRange) => void;
}

type SmartDatePickerPanelProps =
  | SmartDatePickerPanelSingleProps
  | SmartDatePickerPanelRangeProps;

export function SmartDatePickerPanel(props: SmartDatePickerPanelProps) {
  const {
    onClose,
    parseNaturalLanguage,
    autoFocus = false,
    direction = "past",
  } = props;

  const mode = props.mode ?? "single";
  const value = props.value;
  const onChange = props.onChange;
  // The clinic's today on the enclosing scope's clock, so "today" and the
  // presets in a form about one location use that location's day.
  const clock = useClinicClock();
  const todayMarker = () => dayMarker(clock.today());

  // The month the opening value sits in, if it has one.
  const getInitialValueMonth = (): Date | undefined => {
    if (
      mode === "range" &&
      value &&
      typeof value === "object" &&
      "from" in value
    ) {
      return (value as DateRange).from || undefined;
    }
    return (value as Date) || undefined;
  };

  // Get initial input values from existing value
  const getInitialDateInput = (): string => {
    if (mode === "range") {
      const rangeValue = value as DateRange | undefined;
      return rangeValue?.from ? format(rangeValue.from, "MM/dd/yyyy") : "";
    }
    const singleValue = value as Date | undefined;
    return singleValue ? format(singleValue, "MM/dd/yyyy") : "";
  };

  const getInitialEndDateInput = (): string => {
    if (mode === "range") {
      const rangeValue = value as DateRange | undefined;
      return rangeValue?.to ? format(rangeValue.to, "MM/dd/yyyy") : "";
    }
    return "";
  };

  const [dateInputValue, setDateInputValue] = useState(getInitialDateInput);
  const [endDateInputValue, setEndDateInputValue] = useState(
    getInitialEndDateInput,
  );
  // Without a value the calendar opens on the clinic's today, and an untouched
  // picker keeps following it — a zone that settles, or a clock that crosses
  // into the next month — until the user navigates.
  const [initialValueMonth] = useState(getInitialValueMonth);
  const today = clock.today();
  const defaultMonth = useMemo(
    () => initialValueMonth ?? dayMarker(today),
    [initialValueMonth, today],
  );
  const [calendarMonth, setCalendarMonth] = useFollowingDefault<
    Date | undefined
  >(defaultMonth);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const endDateInputRef = useRef<HTMLInputElement>(null);
  const { isMobile } = useIsMobile();
  // How far the range layout has had to shrink to fit its container (e.g. a
  // filter popover capped at the space left on screen): 0 shows two months,
  // 1 one month, 2 also stacks the presets above the calendar. Only ever
  // steps down while mounted, so the layout can't oscillate.
  const [rangeShrink, setRangeShrink] = useState(0);
  const rangeRootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = rangeRootRef.current;
    if (mode !== "range" || !el || rangeShrink >= 2) return;
    const observer = new ResizeObserver(() => {
      if (el.scrollWidth > el.clientWidth + 1) setRangeShrink(rangeShrink + 1);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [mode, rangeShrink]);
  const rangeStacked = rangeShrink >= 2;

  // Get the appropriate presets based on direction
  const rangePresets =
    direction === "future" ? FUTURE_RANGE_PRESETS : PAST_RANGE_PRESETS;

  // Format date input with automatic slashes
  const formatDateInput = (input: string): string => {
    // Remove all non-numeric characters
    const numbers = input.replace(/\D/g, "");

    // Apply MM/DD/YYYY formatting
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 4) {
      return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
    } else {
      return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
    }
  };

  // Parse MM/DD/YYYY format specifically
  const parseMMDDYYYY = (input: string): Date | null => {
    const match = input.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return null;

    const month = parseInt(match[1], 10);
    const day = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);

    // Validate ranges
    if (
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31 ||
      year < 1900 ||
      year > 2100
    ) {
      return null;
    }

    const date = new Date(year, month - 1, day);
    // Check if the date is valid (handles invalid dates like Feb 30)
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }

    return date;
  };

  // Auto-blur the date input when component mounts if not auto-focusing
  useEffect(() => {
    if (!autoFocus && dateInputRef.current) {
      dateInputRef.current.blur();
    }
  }, [autoFocus]);

  // Clear input when component unmounts or closes
  useEffect(() => {
    return () => {
      setDateInputValue("");
      setEndDateInputValue("");
    };
  }, []);

  // Handle date input change for single mode
  const handleSingleDateInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const inputDate = e.target.value;
    const formattedDate = formatDateInput(inputDate);
    setDateInputValue(formattedDate);

    // Try to parse MM/DD/YYYY format first
    const parsedMMDDYYYY = parseMMDDYYYY(formattedDate);
    if (parsedMMDDYYYY) {
      // Navigate calendar to the correct month/year
      setCalendarMonth(parsedMMDDYYYY);
      // Auto-select the date without closing the calendar
      if (mode === "single" && onChange) {
        (onChange as (date: Date) => void)(parsedMMDDYYYY);
      }
      return;
    }

    // If not a complete MM/DD/YYYY, try to navigate to partial date
    const numbers = formattedDate.replace(/\D/g, "");
    const currentYear = todayMarker().getFullYear();

    if (numbers.length >= 1) {
      // Check if we have a valid month (1-2 digits)
      if (numbers.length <= 2) {
        const month = parseInt(numbers, 10);
        if (month >= 1 && month <= 12) {
          // Navigate to the specified month
          const navDate = new Date(currentYear, month - 1, 1);
          setCalendarMonth(navDate);
        }
      } else if (numbers.length >= 4) {
        // At least MM/DD entered, navigate to that month
        const month = parseInt(numbers.slice(0, 2), 10);
        const day = parseInt(numbers.slice(2, 4), 10);

        if (month >= 1 && month <= 12) {
          const navDate = new Date(currentYear, month - 1, Math.min(day, 1));
          setCalendarMonth(navDate);
        }
      }
    }

    // Try to parse with natural language as fallback
    if (parseNaturalLanguage) {
      const parsed = parseNaturalLanguage(formattedDate);
      if (parsed && mode === "single" && onChange) {
        (onChange as (date: Date) => void)(parsed);
      }
    }
  };

  // Handle start date input change for range mode
  const handleRangeStartDateInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const inputDate = e.target.value;
    const formattedDate = formatDateInput(inputDate);
    setDateInputValue(formattedDate);

    const parsedDate = parseMMDDYYYY(formattedDate);
    if (parsedDate) {
      setCalendarMonth(parsedDate);
      if (mode === "range" && onChange) {
        const currentRange = (value as DateRange) || {};
        (onChange as (range: DateRange) => void)({
          from: parsedDate,
          to: currentRange.to,
        });
      }
    }
  };

  // Handle end date input change for range mode
  const handleRangeEndDateInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const inputDate = e.target.value;
    const formattedDate = formatDateInput(inputDate);
    setEndDateInputValue(formattedDate);

    const parsedDate = parseMMDDYYYY(formattedDate);
    if (parsedDate) {
      if (mode === "range" && onChange) {
        const currentRange = (value as DateRange) || {};
        (onChange as (range: DateRange) => void)({
          from: currentRange.from,
          to: parsedDate,
        });
      }
    }
  };

  const handleDateInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    setInputValue: (value: string) => void,
  ) => {
    if (e.key === "Backspace") {
      const cursorPosition = (e.target as HTMLInputElement).selectionStart || 0;
      const inputValue = (e.target as HTMLInputElement).value;

      // If backspacing over a slash, remove the number before it too
      if (cursorPosition > 0 && inputValue[cursorPosition - 1] === "/") {
        e.preventDefault();
        const newValue =
          inputValue.slice(0, cursorPosition - 2) +
          inputValue.slice(cursorPosition);
        const formatted = formatDateInput(newValue);
        setInputValue(formatted);
      }
    }
  };

  const handleTodayClick = () => {
    const today = todayMarker();
    const todayFormatted = format(today, "MM/dd/yyyy");
    setDateInputValue(todayFormatted);
    setCalendarMonth(today);

    if (mode === "single" && onChange) {
      (onChange as (date: Date) => void)(today);
    } else if (mode === "range" && onChange) {
      // In range mode, set today as the start date
      const currentRange = (value as DateRange) || {};
      (onChange as (range: DateRange) => void)({
        from: today,
        to: currentRange.to,
      });
    }
  };

  if (mode === "range") {
    const rangeValue = value as DateRange | undefined;

    const handlePresetClick = (preset: RangePreset) => {
      const range = preset.getRange(todayMarker());
      if (onChange) {
        (onChange as (range: DateRange) => void)(range);
      }
      if (range.from) {
        setDateInputValue(format(range.from, "MM/dd/yyyy"));
        setCalendarMonth(range.from);
      }
      if (range.to) {
        setEndDateInputValue(format(range.to, "MM/dd/yyyy"));
      }
      onClose?.();
    };

    return (
      <div
        ref={rangeRootRef}
        className={cx("flex gap-4", rangeStacked && "flex-col gap-3")}
      >
        {/* Presets sidebar */}
        <div
          className={cx(
            "flex shrink-0 gap-1 whitespace-nowrap border-border",
            rangeStacked
              ? "flex-row flex-wrap border-b pb-3"
              : "flex-col border-r pr-4",
          )}
        >
          <span
            className={cx(
              "mb-1 text-caption-strong text-muted-foreground",
              rangeStacked && "w-full",
            )}
          >
            Quick select
          </span>
          {rangePresets.map((preset) => (
            <button
              key={preset.label}
              type="button"
              onClick={() => handlePresetClick(preset)}
              className="rounded-md px-3 py-1.5 text-left text-body text-foreground-secondary hover:bg-hover"
            >
              {preset.label}
            </button>
          ))}
        </div>

        {/* Main content */}
        <div className="min-w-0 flex-1 space-y-4">
          <div className="flex gap-4">
            <div className="min-w-0 flex-1">
              <Label className="block text-caption">Start Date</Label>
              <Input
                ref={dateInputRef}
                className="w-full"
                type="text"
                placeholder="MM/DD/YYYY"
                value={dateInputValue}
                autoFocus={autoFocus}
                maxLength={10}
                onChange={handleRangeStartDateInputChange}
                onKeyDown={(e) => handleDateInputKeyDown(e, setDateInputValue)}
              />
            </div>
            <div className="flex items-end pb-2 text-foreground-muted">→</div>
            <div className="min-w-0 flex-1">
              <Label className="block text-caption">End Date</Label>
              <Input
                ref={endDateInputRef}
                className="w-full"
                type="text"
                placeholder="MM/DD/YYYY"
                value={endDateInputValue}
                maxLength={10}
                onChange={handleRangeEndDateInputChange}
                onKeyDown={(e) =>
                  handleDateInputKeyDown(e, setEndDateInputValue)
                }
              />
            </div>
          </div>

          {/* Range Calendar */}
          <div className="rounded-md bg-background/70">
            <Calendar
              mode="range"
              selected={rangeValue}
              month={calendarMonth}
              onMonthChange={setCalendarMonth}
              onSelect={(range: DateRange | undefined) => {
                if (range && onChange) {
                  (onChange as (range: DateRange) => void)(range);
                  if (range.from) {
                    setDateInputValue(format(range.from, "MM/dd/yyyy"));
                  }
                  if (range.to) {
                    setEndDateInputValue(format(range.to, "MM/dd/yyyy"));
                  }
                }
                if (range?.from && range?.to) {
                  onClose?.();
                }
              }}
              numberOfMonths={isMobile || rangeShrink > 0 ? 1 : 2}
              initialFocus
              classNames={{
                months: "flex gap-2",
                month: "space-y-2",
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  // Single mode (default)
  const singleValue = value as Date | undefined;

  return (
    <div className="space-y-4">
      <div>
        <Label className="block">Date</Label>
        <div className="flex gap-2">
          <Input
            ref={dateInputRef}
            className="flex-1"
            type="text"
            placeholder="MM/DD/YYYY"
            value={dateInputValue}
            autoFocus={autoFocus}
            maxLength={10}
            onChange={handleSingleDateInputChange}
            onKeyDown={(e) => handleDateInputKeyDown(e, setDateInputValue)}
          />
          <Button variant="secondary" size="sm" onClick={handleTodayClick}>
            Today
          </Button>
        </div>
      </div>

      {/* Single Date Calendar */}
      <div className="rounded-md bg-background/70">
        <Calendar
          mode="single"
          selected={singleValue}
          month={calendarMonth}
          onMonthChange={setCalendarMonth}
          onSelect={(date: Date | undefined) => {
            if (date && onChange) {
              (onChange as (date: Date) => void)(date);
            }
            setDateInputValue(""); // Clear input when date is selected
            onClose?.();
          }}
          numberOfMonths={isMobile ? 1 : 2}
          initialFocus
          classNames={{
            months: "flex gap-2",
            month: "space-y-2",
          }}
        />
      </div>
    </div>
  );
}
